import React, { useState } from "react";

function FAQ() {
    const [expandedFAQs, setExpandedFAQs] = useState({});

    const generalFAQs = [
        {
            question: "What is MintDB?",
            answer: "MintDB is a comprehensive neuropsychiatric mutation database that provides detailed information about genetic variants and their associations with neurological and psychiatric disorders. It integrates mutation data with protein domains, binding sites, AlphaMissense pathogenicity predictions, Biobank phenotype associations, and population frequency data."
        },
        {
            question: "How do I search for mutations in a specific gene?",
            answer: "You can search by gene name, UniProt ID, or specific mutation identifier using the search bar. For advanced queries, use the SQL interface to write custom queries with conditions like 'WHERE gene = 'SCN1A'' to retrieve all mutations in a specific gene. You can also filter by protein domains, pathogenicity scores, or phenotype associations."
        },
        {
            question: "What Biobank data is included in MintDB?",
            answer: "MintDB integrates Biobank phenotype associations for genetic variants, including statistical measures like odds ratios, p-values, and confidence intervals. You can explore associations between specific mutations and various health phenotypes, diseases, and traits from the Biobank study."
        },
        {
            question: "What population frequency data is available?",
            answer: "MintDB includes allele frequency data from major population databases including gnomAD, showing the prevalence of variants across different populations and ethnic groups. Frequency maps help distinguish common variants from rare disease-causing mutations."
        },
        {
            question: "Can I export or download search results?",
            answer: "Yes, you can export search results and SQL query outputs to various formats (CSV, JSON) for downstream analysis. We ask that you cite MintDB in any resulting publications."
        },
        {
            question: "How do I find pathogenic mutations associated with a specific disorder?",
            answer: "Combine multiple filters: search for the disorder name in Biobank phenotypes, filter by AlphaMissense pathogenic classification (score > 0.564), and optionally restrict to specific genes or protein domains. SQL queries allow for complex multi-criteria searches across all these dimensions."
        },
        {
            question: "How often is the database updated?",
            answer: "MintDB is regularly updated with new research findings, variant classifications, and population frequency data. Major updates incorporating new AlphaMissense predictions and Biobank releases occur quarterly, with minor updates as new curated data becomes available."
        }
    ];

    const sqlFAQs = [
        {
            question: "What is SQL?",
            answer: "SQL (Structured Query Language) is a standard programming language used to manage and query databases. It allows you to retrieve, filter, join, and analyze data using simple commands like SELECT, WHERE, and JOIN. No prior programming experience is required - SQL uses intuitive, English-like syntax that's easy to learn for researchers."
        },
        {
            question: "Does MintDB support SQL queries?",
            answer: "Yes! MintDB supports SQL querying for advanced data retrieval. You can write custom SQL queries to filter mutations based on genes, domains, pathogenicity scores, Biobank associations, and more. This allows for complex analyses beyond the standard search interface."
        },
        {
            question: "What SQL tables and columns are available?",
            answer: "MintDB provides access to several key tables including mutations (with columns like gene, uniprot_id, mutation, am_class, am_pathogenicity), protein domains (pfam_id, domain_name), binding sites, Biobank phenotypes, and frequency data. Use the SQL interface to explore the full schema and available columns."
        },
        {
            question: "How do I query mutations in a specific gene using SQL?",
            answer: "Use a SELECT statement with a WHERE clause: 'SELECT * FROM mutations WHERE gene = 'SCN1A''. You can add additional conditions to filter by pathogenicity, domain, or other criteria. For example: 'SELECT * FROM mutations WHERE gene = 'SCN1A' AND am_pathogenicity > 0.564'."
        },
        {
            question: "Can I query multiple genes at once with SQL?",
            answer: "Yes! Use the IN operator to query multiple genes: 'SELECT * FROM mutations WHERE gene IN ('SCN1A', 'SCN2A', 'SCN3A')'. This is particularly useful for studying related proteins, gene families, or disease-associated gene sets."
        },
        {
            question: "How do I join tables in SQL queries?",
            answer: "You can join tables using standard SQL JOIN syntax. For example, to get mutations with their protein domains: 'SELECT m.*, d.pfam_id, d.domain_name FROM mutations m JOIN domains d ON m.uniprot_id = d.uniprot_id WHERE d.pfam_id = 'PF00520''. This allows complex multi-table queries."
        },
        {
            question: "Can I filter by AlphaMissense scores using SQL?",
            answer: "Yes! Use the am_pathogenicity column: 'SELECT * FROM mutations WHERE am_pathogenicity > 0.564' for pathogenic variants, 'WHERE am_pathogenicity < 0.34' for benign, or 'WHERE am_pathogenicity BETWEEN 0.34 AND 0.564' for ambiguous classifications."
        },
        {
            question: "How do I query Biobank associations with SQL?",
            answer: "Join the mutations table with the Biobank phenotypes table and filter by phenotype or statistical significance. For example: 'SELECT m.*, ukb.phenotype, ukb.pvalue FROM mutations m JOIN ukb_phenotypes ukb ON m.mutation_id = ukb.mutation_id WHERE ukb.pvalue < 0.05'."
        },
        {
            question: "Can I export SQL query results?",
            answer: "Yes! All SQL query results can be exported to CSV or JSON format directly from the SQL interface. Simply run your query and use the export button to download the results for further analysis in your preferred tools."
        },
        {
            question: "Are there any SQL query limits or restrictions?",
            answer: "For performance reasons, queries may have row limits on results returned. Complex queries with multiple joins are supported, but very large result sets may be paginated. Use LIMIT clauses to control result size and optimize query performance."
        },
        {
            question: "Can I use SQL functions and aggregations?",
            answer: "Yes! MintDB supports standard SQL functions including COUNT, AVG, MIN, MAX, GROUP BY, and ORDER BY. For example: 'SELECT gene, COUNT(*) as mutation_count FROM mutations GROUP BY gene ORDER BY mutation_count DESC' to count mutations per gene."
        }
    ];

    const toggleFAQ = (index, section) => {
        const key = `${section}-${index}`;
        setExpandedFAQs(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: '#F9F9F9',
            paddingBottom: '3rem'
        }}>
            <div className="container">
                {/* Header Section */}
                <div className="row justify-content-center text-center" style={{paddingTop: '4rem', paddingBottom: '2rem'}}>
                    <div className="col-lg-8">
                        <h1 style={{
                            fontSize: '3rem',
                            fontWeight: '700',
                            color: '#1A2940',
                            marginBottom: '1rem'
                        }}>
                            Frequently Asked Questions
                        </h1>
                        <p style={{
                            fontSize: '1rem',
                            color: 'grey',
                            lineHeight: '1.6',
                            maxWidth: '600px',
                            margin: '0 auto'
                        }}>
                            Find answers to common questions about MintDB, our data, and how to use the platform effectively.
                        </p>
                    </div>
                </div>

                <hr style={{
                    borderColor: '#eef2f6',
                    margin: '2rem 0'
                }} />

            {/* General FAQ Section */}
            <div className="row justify-content-center" style={{marginBottom: '3rem'}}>
                <div className="col-lg-10">
                    <h2 style={{
                        fontSize: '2rem',
                        fontWeight: '700',
                        color: '#1A2940',
                        marginBottom: '1.5rem',
                        borderBottom: '3px solid #60a5fa',
                        paddingBottom: '0.5rem',
                        display: 'inline-block'
                    }}>
                        General Questions
                    </h2>
                    <div style={{padding: '0'}}>
                        {generalFAQs.map((faq, index) => (
                            <div
                                key={index}
                                style={{
                                    backgroundColor: 'white',
                                    border: '1px solid #eef2f6',
                                    borderRadius: '12px',
                                    marginBottom: '1rem',
                                    overflow: 'hidden',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                                }}
                            >
                                {/* Question */}
                                <div
                                    onClick={() => toggleFAQ(index, 'general')}
                                    style={{
                                        cursor: 'pointer',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '1.5rem',
                                        backgroundColor: expandedFAQs[`general-${index}`] ? '#F9F9F9' : 'white',
                                        transition: 'background-color 0.2s ease'
                                    }}
                                >
                                    <h5 style={{
                                        color: '#1A2940',
                                        fontWeight: 'bold',
                                        margin: '0',
                                        fontSize: '1.1rem'
                                    }}>
                                        {faq.question}
                                    </h5>

                                    <span style={{
                                        fontSize: '1.5rem',
                                        color: '#60a5fa',
                                        fontWeight: 'bold',
                                        transform: expandedFAQs[`general-${index}`] ? 'rotate(45deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.3s ease',
                                        minWidth: '30px',
                                        textAlign: 'center'
                                    }}>
                                        +
                                    </span>
                                </div>

                                {/* Answer */}
                                {expandedFAQs[`general-${index}`] && (
                                    <div style={{
                                        padding: '0 1.5rem 1.5rem 1.5rem',
                                        backgroundColor: 'white',
                                        borderTop: '1px solid #eef2f6',
                                        animation: 'fadeIn 0.3s ease-in-out'
                                    }}>
                                        <p style={{
                                            color: 'grey',
                                            fontSize: '0.95rem',
                                            lineHeight: '1.6',
                                            margin: '1rem 0 0 0'
                                        }}>
                                            {faq.answer}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* SQL FAQ Section */}
            <div className="row justify-content-center" id="sql-queries">
                <div className="col-lg-10">
                    <h2 style={{
                        fontSize: '2rem',
                        fontWeight: '700',
                        color: '#1A2940',
                        marginBottom: '1.5rem',
                        borderBottom: '3px solid #60a5fa',
                        paddingBottom: '0.5rem',
                        display: 'inline-block'
                    }}>
                        SQL Queries
                    </h2>
                    <div style={{padding: '0'}}>
                        {sqlFAQs.map((faq, index) => (
                            <div
                                key={index}
                                style={{
                                    backgroundColor: 'white',
                                    border: '1px solid #eef2f6',
                                    borderRadius: '12px',
                                    marginBottom: '1rem',
                                    overflow: 'hidden',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                                }}
                            >
                                {/* Question */}
                                <div
                                    onClick={() => toggleFAQ(index, 'sql')}
                                    style={{
                                        cursor: 'pointer',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '1.5rem',
                                        backgroundColor: expandedFAQs[`sql-${index}`] ? '#F9F9F9' : 'white',
                                        transition: 'background-color 0.2s ease'
                                    }}
                                >
                                    <h5 style={{
                                        color: '#1A2940',
                                        fontWeight: 'bold',
                                        margin: '0',
                                        fontSize: '1.1rem'
                                    }}>
                                        {faq.question}
                                    </h5>

                                    <span style={{
                                        fontSize: '1.5rem',
                                        color: '#60a5fa',
                                        fontWeight: 'bold',
                                        transform: expandedFAQs[`sql-${index}`] ? 'rotate(45deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.3s ease',
                                        minWidth: '30px',
                                        textAlign: 'center'
                                    }}>
                                        +
                                    </span>
                                </div>

                                {/* Answer */}
                                {expandedFAQs[`sql-${index}`] && (
                                    <div style={{
                                        padding: '0 1.5rem 1.5rem 1.5rem',
                                        backgroundColor: 'white',
                                        borderTop: '1px solid #eef2f6',
                                        animation: 'fadeIn 0.3s ease-in-out'
                                    }}>
                                        <p style={{
                                            color: 'grey',
                                            fontSize: '0.95rem',
                                            lineHeight: '1.6',
                                            margin: '1rem 0 0 0'
                                        }}>
                                            {faq.answer}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            </div>


            <style jsx>{`
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </div>
    );
}

export default FAQ;